import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminService, providersService } from '../../services';

export const fetchAdminPlans = createAsyncThunk(
  'admin/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.listPlans();
      if (!data.success) throw new Error(data.message || 'Failed to load plans');
      // Handle both formats: data.plans (old local) and data.data.plans (Lambda)
      const plans = data.plans || data.data?.plans || [];
      // Normalize plan IDs to ensure both _id and id are available
      return plans.map(plan => ({
        ...plan,
        _id: plan._id || plan.id,
        id: plan.id || plan._id,
      }));
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const mutateAdminPlan = createAsyncThunk(
  'admin/mutatePlan',
  async (payload, { rejectWithValue }) => {
    try {
      console.log('mutateAdminPlan: Sending payload:', JSON.stringify(payload, null, 2));
      const response = await adminService.mutatePlan(payload);
      console.log('mutateAdminPlan: Raw response:', response);

      // Handle response structure: { success: true, data: {...} } or { success: false, message: '...' }
      if (!response.success) {
        const errorMsg = response.message || response.error || 'Plan mutation failed';
        console.error('mutateAdminPlan: Error response:', errorMsg);
        return rejectWithValue(errorMsg);
      }

      // Extract data from response
      const data = response.data || response;
      return { payload, data };
    } catch (e) {
      console.error('mutateAdminPlan: Exception:', e);
      const errorMsg = e.message || e.toString();
      console.error('mutateAdminPlan: Error message:', errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const fetchAdminUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.listUsers();
      return data.users || [];
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const createAdminUser = createAsyncThunk(
  'admin/createUser',
  async (user, { rejectWithValue }) => {
    try {
      const data = await adminService.createUser(user);
      if (!data.success) throw new Error(data.message || 'Create user failed');
      return data.user;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const updateAdminUser = createAsyncThunk(
  'admin/updateUser',
  async ({ id, user }, { rejectWithValue }) => {
    try {
      const data = await adminService.updateUser(id, user);
      if (!data.success) throw new Error(data.message || 'Update user failed');
      return data.user;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const deleteAdminUser = createAsyncThunk(
  'admin/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      const data = await adminService.deleteUser(id);
      if (!data.success) throw new Error(data.message || 'Delete user failed');
      return id;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const sendPaymentLink = createAsyncThunk(
  'admin/sendPaymentLink',
  async ({ id, purpose, amount, currency = 'INR', notes }, { rejectWithValue }) => {
    try {
      const data = await adminService.sendPaymentLink(id, { purpose, amount, currency, notes });
      if (!data.success) throw new Error(data.message || 'Payment link failed');
      return { link: data.link, purpose, amount, currency };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const createAgentForUser = createAsyncThunk(
  'admin/createAgentForUser',
  async ({ id, agent }, { rejectWithValue }) => {
    try {
      const data = await adminService.createAgentForUser(id, agent);
      if (!data.success) throw new Error(data.message || 'Create agent failed');
      return data.agent;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchAdminProviders = createAsyncThunk(
  'admin/fetchProviders',
  async (_, { rejectWithValue }) => {
    try {
      // Use admin service to get providers (calls GET /api/admin/providers)
      const response = await adminService.getProviders();

      // Handle different response formats:
      // Lambda format: { success: true, data: { providers: [...] } }
      // Express format: { success: true, data: { providers: [...] }, message: "..." }
      // Old format (with grouped): { success: true, data: { ai: [...], stt: [...], tts: [...] }, providers: [...] }
      let providers = [];

      if (response?.data?.providers && Array.isArray(response.data.providers)) {
        // Standard format: data.providers
        providers = response.data.providers;
      } else if (response?.providers && Array.isArray(response.providers)) {
        // Root level providers (old format)
        providers = response.providers;
      } else if (response?.data?.ai || response?.data?.stt || response?.data?.tts) {
        // Grouped format - flatten it
        providers = [
          ...(response.data.ai || []),
          ...(response.data.stt || []),
          ...(response.data.tts || []),
        ];
      }

      return providers;
    } catch (e) {
      // Fallback to public endpoint if admin endpoint fails
      try {
        const publicData = await providersService.list();
        // Extract only the flat providers array, ignore grouped format
        let providers = publicData.data?.providers || [];
        // If grouped format, flatten it
        if (
          !providers.length &&
          (publicData.data?.ai || publicData.data?.stt || publicData.data?.tts)
        ) {
          providers = [
            ...(publicData.data.ai || []),
            ...(publicData.data.stt || []),
            ...(publicData.data.tts || []),
          ];
        }
        return providers;
      } catch (fallbackError) {
        return rejectWithValue(e.message || fallbackError.message);
      }
    }
  }
);

export const fetchAdminDefaults = createAsyncThunk(
  'admin/fetchDefaults',
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.getDefaults();
      if (!data.success) throw new Error(data.message || 'Failed to load defaults');
      return data.defaults?.value || null;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

const initialState = {
  plans: [],
  users: [],
  providers: [],
  defaults: null,
  loading: false,
  error: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAdminPlans.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchAdminPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(mutateAdminPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mutateAdminPlan.fulfilled, (state, action) => {
        state.loading = false;
        const { payload, data } = action.payload;
        if (payload.action === 'create') {
          state.plans.unshift(data.plan);
        } else if (payload.action === 'update') {
          const planId = payload.id;
          state.plans = state.plans.map(p => {
            const pId = p._id || p.id;
            return pId === planId ? data.plan : p;
          });
        } else if (payload.action === 'delete') {
          const planId = payload.id;
          state.plans = state.plans.filter(p => {
            const pId = p._id || p.id;
            return pId !== planId;
          });
        }
      })
      .addCase(mutateAdminPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchAdminUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        state.users.unshift(action.payload);
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        state.users = state.users.map(u => (u._id === action.payload._id ? action.payload : u));
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.users = state.users.filter(u => u._id !== action.payload);
      });
    builder
      .addCase(fetchAdminProviders.fulfilled, (state, action) => {
        state.providers = action.payload;
      })
      .addCase(fetchAdminDefaults.fulfilled, (state, action) => {
        state.defaults = action.payload;
      });
  },
});

export const selectAdminPlans = state => state?.admin?.plans || [];
export const selectAdminUsers = state => state?.admin?.users || [];
export const selectAdminProviders = state => state?.admin?.providers || [];
export const selectAdminDefaults = state => state?.admin?.defaults || null;
export const selectAdminLoading = state => state?.admin?.loading || false;
export const selectAdminError = state => state?.admin?.error || null;

export default adminSlice.reducer;
